// Browser-compatible JWT utilities
// We don't store any secrets in the frontend code
// All token generation and validation should be done by the backend

/**
 * Base64 URL encode a string
 * @param str The string to encode
 * @returns The encoded string
 */
const base64UrlEncode = (str: string): string => {
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

/**
 * Base64 URL decode a string
 * @param str The string to decode
 * @returns The decoded string
 */
const base64UrlDecode = (str: string): string => {
  // Add padding if needed
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  while (str.length % 4) {
    str += '=';
  }
  return atob(str);
};

/**
 * Decode a JWT token
 * @param token The token to decode
 * @returns The decoded token payload
 */
const decodeToken = (token: string): any | null => {
  try {
    // Split the token into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid token format');
      return null;
    }

    // Decode the payload
    return JSON.parse(base64UrlDecode(parts[1]));
  } catch (error) {
    console.error('JWT decode error:', error);
    return null;
  }
};

/**
 * Check if a JWT token is expired
 * @param token The token to check
 * @returns The decoded token payload if not expired, null otherwise
 */
export const verifyToken = (token: string): any | null => {
  try {
    // Decode the token
    const payload = decodeToken(token);
    if (!payload) {
      return null;
    }

    // Check if the token is expired
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) {
      console.error('Token expired');
      return null;
    }

    return payload;
  } catch (error) {
    console.error('JWT verification error:', error);
    return null;
  }
};

/**
 * Create a temporary token for use when backend authentication fails
 * This is NOT a secure token and should only be used as a fallback
 * @returns A temporary token
 */
export const createTemporaryToken = (): string => {
  // Create a simple token structure without any signature
  // This is just for UI testing when backend is unavailable
  const header = {
    alg: 'none',
    typ: 'JWT'
  };

  const payload = {
    sub: 'admin',
    username: 'admin',
    name: 'Admin User',
    role: 'ADMIN',
    collegeId: 'college-1',
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60) // 24 hours
  };

  // Encode header and payload
  const encodedHeader = base64UrlEncode(JSON.stringify(header));
  const encodedPayload = base64UrlEncode(JSON.stringify(payload));

  // Use an empty signature
  const signature = '';

  // Return the token structure
  return `${encodedHeader}.${encodedPayload}.${signature}`;
};
